import { __decorate } from "tslib";
import { DestroyRef, inject } from '@angular/core';
import { CrudManagerService } from '@common/crud-ui/frontend-shared';
import { MediaAssetProvidersFrontendService, MediaFrontendSharedModule } from '@common/media/frontend-shared';
import { MediaAssetSubtitlesManagerComponent } from '@common/media/frontend-shared/ui';
import { MediaAssetItem, RefreshMediaAssetReadyStateRequest, RemoveMediaAssetMappingByAssetRequest, SyncMediaAssetMetadataRequest } from '@common/media/shared';
import { ConfirmDialogService } from '@common/ui/frontend-shared/confirmdialog';
import { DynamicDialogService } from '@common/ui/frontend-shared/dynamic-dialog';
import { AppRequestService, CrudServiceFactory, provideRootRoutes } from '@core/frontend-shared';
import { FrontendFeaturePlugin } from '@core/frontend-shared/feature';
import { ClassName } from '@core/shared/class-name';
import { take } from 'rxjs';
import * as i0 from "@angular/core";
let MediaFrontendAdminFeature = /*#__PURE__*/(() => {
  let MediaFrontendAdminFeature = class MediaFrontendAdminFeature extends FrontendFeaturePlugin {
    getFeatureContext() {
      return {"package":"@common/media/frontend-admin","featureId":"common-media"};
    }
    getFeatureFunctionality(functionality) {
      const providers = inject(MediaAssetProvidersFrontendService);
      functionality.defineMenuItems({
        adminToolbar: [{
          label: 'Medien-Manager',
          routerLink: 'media',
          icon: 'photo-video',
          priority: 5,
          declareGroup: 'media'
        }]
      });
      functionality.defineItemActions([{
        modelId: 'MediaAssetItem',
        id: 'refresh-state',
        displayContext: ['crud'],
        label: 'Status aktualisieren',
        icon: 'sync-alt',
        enableIf: item => {
          return !item?.ready;
        },
        action: item => {
          const provider = providers.getProvider(item.provider);
          const api = inject(AppRequestService);
          const confirmDialog = inject(ConfirmDialogService);
          const crudManager = inject(CrudManagerService, {
            optional: true
          });
          const crud = crudManager?.crudService || inject(CrudServiceFactory).useDestroyRef(inject(DestroyRef)).createCrudService(MediaAssetItem);
          if (provider.features.checkForReadyness) {
            api.sendRequest(RefreshMediaAssetReadyStateRequest, {
              assetId: item.id
            }).subscribe(response => {
              if (response.ready) {
                // handler of RefreshMediaAssetReadyStateRequest already updates entity ready value.
                // this is a trick to trigger a reload of the item so that CRUD components show it correctly.
                const patch = {
                  id: item.id,
                  ready: true
                };
                crud.save(patch).subscribe();
              }
            });
          } else {
            if (!item.directUrl) {
              alert("Der Asset-Datensatz ist unvollständig. Der Asset-Status kann erst geprüft werden, sobald notwendige Infos vom Host zurückgemeldet wurden.");
            } else {
              confirmDialog.confirm({
                header: 'Status ändern',
                message: `
									Der Status dieses Assets kann nicht geprüft werden, da der Hoster dies nicht unterstützt.
									Wenn Sie sicher sind, dass das Asset fälschlicherweise als in Verarbeitung markiert ist,
									können Sie es manuell auf bereit umstellen.
								`,
                acceptLabel: 'Umstellen auf "bereit"',
                rejectLabel: 'Abbrechen',
                accept: () => {
                  const patch = {
                    id: item.id,
                    ready: true
                  };
                  crud.save(patch).subscribe();
                }
              });
            }
          }
        }
      }, {
        modelId: 'MediaAssetItem',
        id: 'refresh-metadata',
        displayContext: ['crud'],
        label: 'Metadaten aktualisieren',
        icon: 'sync-alt',
        enableIf: item => {
          return !!item?.ready;
        },
        action: item => {
          // const provider = providers.getProvider(item.provider);
          const api = inject(AppRequestService);
          const crudManager = inject(CrudManagerService, {
            optional: true
          });
          api.sendRequest(SyncMediaAssetMetadataRequest, {
            assetId: item.id
          }).subscribe(() => {
            if (crudManager) {
              crudManager.reload();
            }
          });
        }
      }, {
        modelId: 'MediaAssetItem',
        id: 'replace-with',
        displayContext: ['crud'],
        label: 'Durch Asset ersetzen',
        icon: 'exchange-alt',
        action: item => {
          alert('TODO: implement asset replacement. show asset selector with search bar');
        }
      }, {
        modelId: 'MediaAssetItem',
        id: 'remove-usage',
        label: 'Alle Verwendungen löschen',
        icon: 'unlink',
        displayContext: ['crud'],
        enableIf: item => {
          return item?.usage?.length > 0;
        },
        action: item => {
          const crudManager = inject(CrudManagerService, {
            optional: true
          });
          const confirmDialog = inject(ConfirmDialogService);
          const api = inject(AppRequestService);
          const message = 'Möchten Sie wirklich alle Verwendungen des Asset "' + item.name + '" (' + item.id + ') entfernen? Items, die dieses Asset verwenden, verlieren hierdurch ihre Zuordnung.';
          confirmDialog.confirm({
            header: 'Bestätigung erforderlich',
            message,
            accept: () => {
              api.sendRequest(RemoveMediaAssetMappingByAssetRequest, {
                assetId: item.id
              }).subscribe(response => {
                if (crudManager) crudManager.reload(true);
              });
            }
          });
        }
      }, {
        modelId: 'MediaAssetItem',
        id: 'remove-asset',
        label: 'Asset löschen',
        icon: 'trash',
        displayContext: ['crud'],
        enableIf: item => {
          return item?.usage?.length === 0;
        },
        action: item => {
          const crudManager = inject(CrudManagerService, {
            optional: true
          });
          const crud = crudManager?.crudService || inject(CrudServiceFactory).useDestroyRef(inject(DestroyRef)).createCrudService(MediaAssetItem);
          const confirmDialog = inject(ConfirmDialogService);
          const message = 'Möchten Sie das Asset "' + item.name + '" (' + item.id + ') wirklich löschen? TODO: Checkbox for deleting from host too';
          confirmDialog.confirm({
            header: 'Bestätigung erforderlich',
            message,
            accept: () => {
              crud.delete(item).subscribe(response => {
                if (crudManager) crudManager.navigateTo('list');
              });
            }
          });
        }
      }, {
        modelId: 'MediaAssetItem',
        id: 'generate-subtitle',
        label: 'Untertitel bearbeiten',
        icon: 'sync-alt',
        displayContext: ['crud'],
        enableIf: item => {
          return item.type === 'video';
        },
        action: item => {
          const dialogService = inject(DynamicDialogService);
          const crudManager = inject(CrudManagerService);
          crudManager.pauseCrudStateEvents(true);
          const ref = dialogService.open(MediaAssetSubtitlesManagerComponent, {
            data: {
              assetInput: item
            },
            width: '95%',
            height: '95%',
            header: 'Video-Untertitel verwalten',
            removeFocus: true,
            dismissableMask: false,
            padded: false,
            contentStyle: {},
            baseZIndex: 10000
          });
          ref.onClose.pipe(take(1)).subscribe(result => {
            crudManager.pauseCrudStateEvents(false);
          });
        }
      }]);
    }
    static {
      this.ɵfac = /*@__PURE__*/(() => {
        let ɵMediaFrontendAdminFeature_BaseFactory;
        return function MediaFrontendAdminFeature_Factory(__ngFactoryType__) {
          return (ɵMediaFrontendAdminFeature_BaseFactory || (ɵMediaFrontendAdminFeature_BaseFactory = i0.ɵɵgetInheritedFactory(MediaFrontendAdminFeature)))(__ngFactoryType__ || MediaFrontendAdminFeature);
        };
      })();
    }
    static {
      this.ɵmod = /*@__PURE__*/i0.ɵɵdefineNgModule({
        type: MediaFrontendAdminFeature
      });
    }
    static {
      this.ɵinj = /*@__PURE__*/i0.ɵɵdefineInjector({
        providers: [provideRootRoutes([{
          path: 'media',
          defineAddonSlot: 'media',
          children: [{
            path: '',
            loadChildren: () => import('./media-admin.module').then(m => m.MediaFrontendAdminModule)
          }]
        }])
        // provideFeatureAddonRoutes([])
        ],
        imports: [MediaFrontendSharedModule]
      });
    }
  };
  MediaFrontendAdminFeature = __decorate([ClassName("MediaFrontendAdminFeature")], MediaFrontendAdminFeature);
  return MediaFrontendAdminFeature;
})();
export { MediaFrontendAdminFeature };